import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Loader2, Search, Sparkles, ShieldAlert, ExternalLink,
  CheckCircle2, AlertTriangle, RotateCcw, ClipboardList,
} from 'lucide-react';
import toast from 'react-hot-toast';
import apiService from '../services/apiService';
import { useAuth } from '../contexts/AuthContext';
import AvaliacoesHistorico from './AvaliacoesHistorico';

// Mesma ordem/chaves de backend/src/services/perplexityService.js (ATRIBUTOS_PESQUISA_WEB) e dos
// pesos padrão documentados em .clinerules/16-avaliacao-compra-ia.md. Os pesos reais usados no
// cálculo vêm de `avaliacao_compra_parametros` no backend — ainda sem rota GET/PUT exposta, então
// aqui é só o valor padrão para exibição ao lado do slider (ver Status no clinerules).
const ATRIBUTOS_PESQUISA_WEB = [
  { chave: 'volume_vendas_online', label: 'Volume de vendas online', pesoPadrao: 30 },
  { chave: 'nota_qualidade', label: 'Nota / qualidade percebida', pesoPadrao: 25 },
  { chave: 'ausencia_reclamacoes', label: 'Ausência de reclamações recorrentes', pesoPadrao: 20 },
  { chave: 'presenca_grandes_varejistas', label: 'Presença nos grandes varejistas', pesoPadrao: 15 },
  { chave: 'posicionamento_preco', label: 'Posicionamento de preço', pesoPadrao: 10 },
];

const ROTULO_GRUPO = { mensal: 'Mensal', bimestral: 'Bimestral', trimestral: 'Trimestral' };

const ROTULO_VEREDITO = {
  comprar: { label: 'Comprar', className: 'bg-green-50 text-green-700 border-green-300' },
  compra_teste: { label: 'Compra de Teste', className: 'bg-amber-50 text-amber-700 border-amber-300' },
  nao_comprar: { label: 'Não Comprar', className: 'bg-red-50 text-red-700 border-red-300' },
};

function formatarDataHora(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

function formatarData(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('pt-BR');
}

function formatarMoeda(v) {
  if (v == null) return '—';
  return Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function estadoInicialNotas() {
  const notas = {};
  for (const { chave } of ATRIBUTOS_PESQUISA_WEB) {
    notas[chave] = { notaIa: null, notaFinal: 2.5, editado: false };
  }
  return notas;
}

function Secao({ numero, titulo, subtitulo, children }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5 mb-4">
      <div className="flex items-center gap-2 mb-3">
        <span className="w-6 h-6 flex items-center justify-center rounded-full bg-blue-600 text-white text-xs font-semibold flex-shrink-0">
          {numero}
        </span>
        <div>
          <h2 className="text-sm font-semibold text-gray-900">{titulo}</h2>
          {subtitulo && <p className="text-xs text-gray-500">{subtitulo}</p>}
        </div>
      </div>
      {children}
    </div>
  );
}

export default function AvaliarProduto() {
  const navigate = useNavigate();
  const { isGerenteOuAdmin, usuario } = useAuth();
  const permitido = isGerenteOuAdmin();

  const [tab, setTab] = useState('nova'); // 'nova' | 'historico'

  // ── Etapa 1: Identificar ──────────────────────────────────────────────
  const [texto, setTexto] = useState('');
  const [identificando, setIdentificando] = useState(false);
  const [identificado, setIdentificado] = useState(null);
  const [termoPrincipalOriginal, setTermoPrincipalOriginal] = useState('');

  // ── Etapa 2: Candidatos e análogos ────────────────────────────────────
  const [buscandoAnalogos, setBuscandoAnalogos] = useState(false);
  const [resultadoAnalogos, setResultadoAnalogos] = useState(null);
  const [codigoManual, setCodigoManual] = useState('');
  const [ancoraEscolhida, setAncoraEscolhida] = useState(null);
  const [carregandoIndicadores, setCarregandoIndicadores] = useState(false);
  const [indicadores, setIndicadores] = useState(null);

  // ── Etapa 3: Pesquisa na internet ─────────────────────────────────────
  const [pesquisandoWeb, setPesquisandoWeb] = useState(false);
  const [pesquisaWebResultado, setPesquisaWebResultado] = useState(null);
  const [modalPesquisaIndisponivel, setModalPesquisaIndisponivel] = useState(false);
  const [notas, setNotas] = useState(estadoInicialNotas());
  const [grupoEscolhido, setGrupoEscolhido] = useState('');

  // ── Etapa 4: Veredito ──────────────────────────────────────────────────
  const [calculando, setCalculando] = useState(false);
  const [veredito, setVeredito] = useState(null);
  const [decisaoHumana, setDecisaoHumana] = useState('');
  const [observacao, setObservacao] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [avaliacaoSalva, setAvaliacaoSalva] = useState(null);

  const termoFoiEditado = identificado != null && (identificado.termo_principal || '') !== termoPrincipalOriginal;

  function resetarTudo() {
    setTexto('');
    setIdentificado(null);
    setTermoPrincipalOriginal('');
    setResultadoAnalogos(null);
    setCodigoManual('');
    setAncoraEscolhida(null);
    setIndicadores(null);
    setPesquisaWebResultado(null);
    setNotas(estadoInicialNotas());
    setGrupoEscolhido('');
    setVeredito(null);
    setDecisaoHumana('');
    setObservacao('');
    setAvaliacaoSalva(null);
  }

  // ── Handlers ───────────────────────────────────────────────────────────

  async function handleIdentificar() {
    if (!texto.trim()) return;
    setIdentificando(true);
    setIdentificado(null);
    setResultadoAnalogos(null);
    setAncoraEscolhida(null);
    setIndicadores(null);
    setPesquisaWebResultado(null);
    setVeredito(null);
    setAvaliacaoSalva(null);
    try {
      const resp = await apiService.avaliacaoCompra.identificar(texto);
      const dados = resp.data.data;
      setIdentificado(dados);
      setTermoPrincipalOriginal(dados.termo_principal || '');
      if (dados.degradado) {
        toast.error('IA indisponível para identificar o produto. Digite o termo principal manualmente abaixo.');
      }
    } catch {
      toast.error('Erro ao identificar o produto.');
    } finally {
      setIdentificando(false);
    }
  }

  function handleChangeIdentificado(campo, valor) {
    setIdentificado(prev => ({ ...prev, [campo]: valor }));
  }

  async function handleBuscarAnalogos() {
    const termo = (identificado?.termo_principal || '').trim();
    if (!termo) return;

    setBuscandoAnalogos(true);
    setResultadoAnalogos(null);
    setAncoraEscolhida(null);
    setIndicadores(null);
    setVeredito(null);
    try {
      if (termoFoiEditado) {
        // Termo editado manualmente: o endpoint /analogos re-identifica a partir do texto livre
        // original (cache por hash), então não refletiria a edição — usa a busca crua no ERP e o
        // comprador escolhe manualmente (mesmo espírito do modo degradado).
        const resp = await apiService.avaliacaoCompra.candidatos(termo);
        const lista = (resp.data.data || []).slice(0, 15).map(c => ({
          codigo: c.codigo, descricao: c.descricao,
          semRank: true, gmm: undefined, grupo: undefined, estoqueMinimo: undefined, estoqueIdeal: undefined,
        }));
        setResultadoAnalogos({ termoPrincipal: termo, analogos: lista, degradado: false, rankeamentoIaIndisponivel: true });
      } else {
        const resp = await apiService.avaliacaoCompra.analogos(texto);
        setResultadoAnalogos(resp.data.data);
        if (resp.data.data.rankeamentoIaIndisponivel) {
          toast.error('Ranking por IA indisponível — mostrando por similaridade textual.');
        }
      }
    } catch {
      toast.error('Erro ao buscar produtos análogos.');
    } finally {
      setBuscandoAnalogos(false);
    }
  }

  async function carregarIndicadores(codigo) {
    setCarregandoIndicadores(true);
    setIndicadores(null);
    setVeredito(null);
    setAvaliacaoSalva(null);
    try {
      const resp = await apiService.avaliacaoCompra.indicadores(codigo);
      if (!resp.data.data) {
        toast.error('Produto não encontrado ou inativo no ERP.');
        return;
      }
      setIndicadores(resp.data.data);
    } catch {
      toast.error('Erro ao buscar indicadores do produto.');
    } finally {
      setCarregandoIndicadores(false);
    }
  }

  function handleEscolherAncora(analogo) {
    setAncoraEscolhida(analogo);
    setCodigoManual('');
    carregarIndicadores(analogo.codigo);
  }

  function handleUsarCodigoManual() {
    const codigo = codigoManual.trim();
    if (!codigo) return;
    setAncoraEscolhida({ codigo, descricao: null, manual: true });
    carregarIndicadores(codigo);
  }

  async function handlePesquisarWeb() {
    setPesquisandoWeb(true);
    setVeredito(null);
    try {
      const resp = await apiService.avaliacaoCompra.pesquisaWeb(texto);
      const resultado = resp.data.data;
      setPesquisaWebResultado(resultado);

      const porAtributo = new Map((resultado.atributos || []).map(a => [a.atributo, a]));
      const novasNotas = {};
      for (const { chave } of ATRIBUTOS_PESQUISA_WEB) {
        const encontrado = porAtributo.get(chave);
        novasNotas[chave] = {
          notaIa: encontrado ? Number(encontrado.nota) : null,
          notaFinal: encontrado ? Number(encontrado.nota) : 2.5,
          editado: false,
        };
      }
      setNotas(novasNotas);

      if (resultado.degradado) {
        setModalPesquisaIndisponivel(true);
      }
    } catch {
      toast.error('Erro na pesquisa web.');
    } finally {
      setPesquisandoWeb(false);
    }
  }

  function handleSliderChange(chave, valor) {
    setNotas(prev => ({ ...prev, [chave]: { ...prev[chave], notaFinal: Number(valor), editado: true } }));
    setVeredito(null);
  }

  function handleChangeGrupo(valor) {
    setGrupoEscolhido(valor);
    setVeredito(null);
  }

  async function handleCalcular() {
    if (!indicadores || indicadores.semHistorico) return;
    setCalculando(true);
    setAvaliacaoSalva(null);
    try {
      const atributosPayload = ATRIBUTOS_PESQUISA_WEB.map(({ chave }) => ({
        atributo: chave,
        notaFinal: notas[chave]?.notaFinal ?? 2.5,
      }));
      const resp = await apiService.avaliacaoCompra.calcular({
        gmmAncora: indicadores.gmm,
        leadTimeDias: indicadores.leadTimeDias,
        margemSegurancaDias: indicadores.margemSegurancaDias,
        custo: indicadores.custo,
        atributos: atributosPayload,
        grupoEscolhido: grupoEscolhido || null,
      });
      setVeredito(resp.data.data);
      setDecisaoHumana('');
    } catch (e) {
      toast.error(e.response?.data?.error || 'Erro ao calcular veredito.');
    } finally {
      setCalculando(false);
    }
  }

  async function handleSalvar() {
    if (!veredito || !decisaoHumana) {
      toast.error('Calcule o veredito e escolha uma decisão antes de salvar.');
      return;
    }
    setSalvando(true);
    try {
      const atributosPayload = ATRIBUTOS_PESQUISA_WEB.map(({ chave }) => ({
        atributo: chave,
        notaIa: notas[chave]?.notaIa ?? null,
        notaFinal: notas[chave]?.notaFinal ?? 2.5,
        editado: !!notas[chave]?.editado,
      }));

      const fontes = [];
      for (const a of (pesquisaWebResultado?.atributos || [])) {
        for (const f of (a.fontes || [])) fontes.push({ url: f.url, titulo: f.titulo, trecho: f.trecho });
      }

      const analogosConsiderados = (resultadoAnalogos?.analogos || []).map(a => ({
        codigo: a.codigo, descricao: a.descricao, gmm: a.gmm ?? null, grupo: a.grupo ?? null,
        estoqueMinimo: a.estoqueMinimo ?? null, estoqueIdeal: a.estoqueIdeal ?? null,
        score: a.score ?? null, justificativa: a.justificativa || null,
        escolhido: a.codigo === ancoraEscolhida?.codigo,
      }));
      if (ancoraEscolhida?.manual && !analogosConsiderados.some(a => a.codigo === ancoraEscolhida.codigo)) {
        analogosConsiderados.push({
          codigo: ancoraEscolhida.codigo, descricao: indicadores?.nome || null,
          gmm: indicadores?.gmm ?? null, grupo: indicadores?.grupo ?? null,
          estoqueMinimo: indicadores?.estoqueMinimo ?? null, estoqueIdeal: indicadores?.estoqueIdeal ?? null,
          score: null, justificativa: 'Código informado manualmente.', escolhido: true,
        });
      }

      const resp = await apiService.avaliacaoCompra.salvar({
        textoDigitado: texto,
        termoPrincipal: identificado?.termo_principal || null,
        jsonNormalizado: identificado || null,
        codigoAncora: ancoraEscolhida?.codigo || null,
        gmmAncora: indicadores?.gmm ?? null,
        // gmm_data_apuracao é DATE no MySQL — truncar o timestamp ISO (calculadoEm) para "AAAA-MM-DD".
        gmmDataApuracao: indicadores?.calculadoEm ? String(indicadores.calculadoEm).slice(0, 10) : null,
        leadTimeDias: indicadores?.leadTimeDias ?? null,
        margemSegurancaDias: indicadores?.margemSegurancaDias ?? null,
        custo: indicadores?.custo ?? null,
        atributos: atributosPayload,
        grupoEscolhido: grupoEscolhido || null,
        decisaoHumana,
        observacao,
        analogosConsiderados,
        fontes,
      });
      setAvaliacaoSalva(resp.data.data);
      toast.success('Avaliação salva com sucesso!');
    } catch (e) {
      toast.error(e.response?.data?.error || 'Erro ao salvar avaliação.');
    } finally {
      setSalvando(false);
    }
  }

  if (!permitido) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
            <ShieldAlert className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">Acesso restrito</h2>
          <p className="text-gray-600">Esta página é restrita a gerentes e administradores.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Cabeçalho */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 border border-gray-200 rounded-lg px-3 py-1.5 hover:bg-gray-50 transition-colors"
        >
          <ArrowLeft size={15} />Voltar
        </button>
        <div>
          <h1 className="text-xl font-medium text-gray-900 leading-tight">Compras IA</h1>
          <p className="text-sm text-gray-500">
            Avaliação de compra de produto novo — giro de análogos + pesquisa na internet (POP-EST-001 complementar)
          </p>
        </div>
      </div>

      {/* Abas */}
      <div className="flex items-center gap-2 mb-4 border-b border-gray-200">
        <button
          onClick={() => setTab('nova')}
          className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
            tab === 'nova' ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-800'
          }`}
        >
          <Sparkles size={14} />Nova Avaliação
        </button>
        <button
          onClick={() => setTab('historico')}
          className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
            tab === 'historico' ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-800'
          }`}
        >
          <ClipboardList size={14} />Histórico
        </button>
      </div>

      {tab === 'historico' && <AvaliacoesHistorico />}

      {tab === 'nova' && (
        <>
          {/* Etapa 1 — Identificar */}
          <Secao numero={1} titulo="Identificar produto" subtitulo="Descreva o produto que ainda não está cadastrado">
            <div className="flex flex-wrap items-start gap-3">
              <textarea
                value={texto}
                onChange={e => setTexto(e.target.value)}
                placeholder='ex: "ventilador de teto Tramontina 3 pás preto"'
                rows={2}
                className="flex-1 min-w-[280px] border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-400 resize-none"
              />
              <button
                onClick={handleIdentificar}
                disabled={!texto.trim() || identificando}
                className="flex items-center gap-1.5 px-4 py-2 border border-blue-300 bg-blue-50 text-blue-700 rounded-lg text-sm hover:bg-blue-100 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {identificando ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
                Identificar
              </button>
            </div>

            {identificado && (
              <div className="mt-4 bg-gray-50 border border-gray-200 rounded-lg p-4">
                {identificado.degradado && (
                  <p className="flex items-center gap-1.5 text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1.5 mb-3">
                    <AlertTriangle size={13} />IA indisponível — digite o termo principal manualmente para prosseguir.
                  </p>
                )}
                {identificado.deCache && (
                  <p className="text-xs text-gray-400 mb-2">Resultado veio do cache (mesma consulta recente).</p>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <label className="text-xs text-gray-600 flex flex-col gap-1">
                    Termo principal *
                    <input
                      type="text"
                      value={identificado.termo_principal || ''}
                      onChange={e => handleChangeIdentificado('termo_principal', e.target.value)}
                      className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:border-blue-400"
                    />
                  </label>
                  <label className="text-xs text-gray-600 flex flex-col gap-1">
                    Categoria
                    <input
                      type="text"
                      value={identificado.categoria || ''}
                      onChange={e => handleChangeIdentificado('categoria', e.target.value)}
                      className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:border-blue-400"
                    />
                  </label>
                  <label className="text-xs text-gray-600 flex flex-col gap-1">
                    Marca
                    <input
                      type="text"
                      value={identificado.marca || ''}
                      onChange={e => handleChangeIdentificado('marca', e.target.value)}
                      className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:border-blue-400"
                    />
                  </label>
                </div>
                {identificado.atributos?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {identificado.atributos.map((a, i) => (
                      <span key={i} className="text-xs bg-white border border-gray-200 rounded px-2 py-0.5 text-gray-600">{a}</span>
                    ))}
                  </div>
                )}
                {termoFoiEditado && (
                  <p className="text-xs text-amber-700 mt-2">
                    Termo editado manualmente — a busca de análogos na próxima etapa usará busca direta no ERP, sem ranking por IA.
                  </p>
                )}
              </div>
            )}
          </Secao>

          {/* Etapa 2 — Candidatos e análogos */}
          {identificado && (
            <Secao numero={2} titulo="Candidatos e análogos" subtitulo="Escolha o produto já cadastrado que serve de âncora de giro">
              <button
                onClick={handleBuscarAnalogos}
                disabled={!identificado.termo_principal?.trim() || buscandoAnalogos}
                className="flex items-center gap-1.5 px-4 py-2 border border-blue-300 bg-blue-50 text-blue-700 rounded-lg text-sm hover:bg-blue-100 disabled:opacity-40 disabled:cursor-not-allowed mb-3"
              >
                {buscandoAnalogos ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
                Buscar análogos
              </button>

              {resultadoAnalogos?.rankeamentoIaIndisponivel && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 mb-3">
                  Ranking por IA indisponível — lista abaixo ordenada por similaridade textual simples.
                </p>
              )}

              {resultadoAnalogos && resultadoAnalogos.analogos.length === 0 && (
                <p className="text-sm text-gray-400 mb-3">Nenhum produto semelhante encontrado no ERP. Digite um código manualmente abaixo.</p>
              )}

              {resultadoAnalogos && resultadoAnalogos.analogos.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
                  {resultadoAnalogos.analogos.map(a => {
                    const selecionado = ancoraEscolhida?.codigo === a.codigo && !ancoraEscolhida?.manual;
                    return (
                      <button
                        key={a.codigo}
                        onClick={() => handleEscolherAncora(a)}
                        className={`text-left border rounded-lg p-3 text-sm transition-colors ${
                          selecionado ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        <div className="font-medium text-gray-900">{a.descricao || '—'}</div>
                        <div className="text-xs text-gray-400 mb-1">Código {a.codigo}</div>
                        {!a.semRank && (
                          <div className="text-xs text-gray-600 flex flex-wrap gap-x-3">
                            <span>GMM: {a.gmm != null ? Number(a.gmm).toFixed(2) : '—'}</span>
                            <span>Grupo: {ROTULO_GRUPO[a.grupo] || '—'}</span>
                            {a.score != null && <span>Similaridade: {(Number(a.score) * 100).toFixed(0)}%</span>}
                          </div>
                        )}
                        {a.justificativa && <div className="text-xs text-gray-400 mt-1 italic">{a.justificativa}</div>}
                        {selecionado && (
                          <div className="mt-1 flex items-center gap-1 text-xs text-blue-700 font-medium">
                            <CheckCircle2 size={12} />Âncora escolhida
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}

              <div className="flex flex-wrap items-end gap-2 pt-2 border-t border-gray-100">
                <label className="text-xs text-gray-600 flex flex-col gap-1">
                  Ou digite o código do produto manualmente
                  <input
                    type="text"
                    value={codigoManual}
                    onChange={e => setCodigoManual(e.target.value)}
                    placeholder="Código no ERP"
                    className="w-40 border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:border-blue-400"
                  />
                </label>
                <button
                  onClick={handleUsarCodigoManual}
                  disabled={!codigoManual.trim()}
                  className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Usar este código
                </button>
              </div>

              {carregandoIndicadores && (
                <div className="flex items-center gap-2 text-sm text-gray-500 mt-3">
                  <Loader2 size={14} className="animate-spin" />Carregando indicadores...
                </div>
              )}

              {indicadores && (
                <div className="mt-4 bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <div className="font-medium text-gray-900 mb-1">{indicadores.nome}</div>
                  <div className="text-xs text-gray-400 mb-3">Código {indicadores.codigo}</div>

                  {indicadores.semHistorico ? (
                    <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1.5">
                      Este produto não tem GMM calculado (sem histórico ou ainda não entrou no cálculo semanal). Escolha outro análogo.
                    </p>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                      <div><span className="text-xs text-gray-500 block">GMM</span>{Number(indicadores.gmm).toFixed(2)}</div>
                      <div><span className="text-xs text-gray-500 block">Grupo</span>{ROTULO_GRUPO[indicadores.grupo] || '—'}</div>
                      <div><span className="text-xs text-gray-500 block">Estoque Mínimo</span>{indicadores.estoqueMinimo ?? '—'}</div>
                      <div><span className="text-xs text-gray-500 block">Estoque Ideal</span>{indicadores.estoqueIdeal ?? '—'}</div>
                      <div><span className="text-xs text-gray-500 block">Custo</span>{formatarMoeda(indicadores.custo)}</div>
                      <div><span className="text-xs text-gray-500 block">Preço</span>{formatarMoeda(indicadores.preco)}</div>
                      <div><span className="text-xs text-gray-500 block">Lead Time</span>{indicadores.leadTimeDias} dias</div>
                      <div><span className="text-xs text-gray-500 block">Margem Segurança</span>{indicadores.margemSegurancaDias} dias</div>
                    </div>
                  )}

                  <p className="text-xs text-gray-400 mt-3">
                    Giro apurado em {formatarData(indicadores.calculadoEm)} · último cálculo geral em {formatarDataHora(indicadores.ultimoCalculoGeral)}
                  </p>
                  {indicadores.avisoRodadaAtrasada && (
                    <p className="flex items-center gap-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 mt-2">
                      <AlertTriangle size={13} />O cálculo semanal de GMM está atrasado — o veredito sai, mas com essa ressalva.
                    </p>
                  )}
                </div>
              )}
            </Secao>
          )}

          {/* Etapa 3 — Pesquisa na internet */}
          {indicadores && !indicadores.semHistorico && (
            <Secao numero={3} titulo="Pesquisa na internet" subtitulo="Atributos do produto digitado, com nota de 0 a 5 e fontes">
              <button
                onClick={handlePesquisarWeb}
                disabled={pesquisandoWeb}
                className="flex items-center gap-1.5 px-4 py-2 border border-blue-300 bg-blue-50 text-blue-700 rounded-lg text-sm hover:bg-blue-100 disabled:opacity-40 disabled:cursor-not-allowed mb-4"
              >
                {pesquisandoWeb ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                Pesquisar atributos na internet
              </button>

              {pesquisaWebResultado?.degradado && (
                <p className="flex items-center gap-1.5 text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1.5 mb-3">
                  <AlertTriangle size={13} />Pesquisa web indisponível — notas neutras (2,5). Ajuste manualmente se tiver informação.
                </p>
              )}

              {pesquisaWebResultado && (
                <div className="space-y-4">
                  {ATRIBUTOS_PESQUISA_WEB.map(({ chave, label, pesoPadrao }) => {
                    const nota = notas[chave];
                    const fontesAtributo = pesquisaWebResultado.atributos?.find(a => a.atributo === chave)?.fontes || [];
                    return (
                      <div key={chave}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm text-gray-700">{label} <span className="text-xs text-gray-400">(peso padrão {pesoPadrao}%)</span></span>
                          <span className="text-sm font-medium text-gray-900">
                            {nota.notaFinal.toFixed(1)}
                            {nota.editado && <span className="text-xs text-amber-600 ml-1">(editado)</span>}
                          </span>
                        </div>
                        <input
                          type="range"
                          min={0}
                          max={5}
                          step={0.5}
                          value={nota.notaFinal}
                          onChange={e => handleSliderChange(chave, e.target.value)}
                          className="w-full accent-blue-600"
                        />
                        <div className="flex items-center justify-between text-xs text-gray-400">
                          <span>{nota.notaIa != null ? `IA sugeriu: ${nota.notaIa.toFixed(1)}` : 'Sem sinal da IA para este atributo (nota neutra)'}</span>
                        </div>
                        {fontesAtributo.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-1">
                            {fontesAtributo.map((f, i) => (
                              <a
                                key={i}
                                href={f.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                title={f.trecho || f.titulo}
                                className="flex items-center gap-1 text-xs text-blue-600 hover:underline"
                              >
                                <ExternalLink size={11} />{f.titulo || 'Fonte'}
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="mt-5 pt-4 border-t border-gray-100">
                <label className="text-sm text-gray-600 flex flex-col gap-1 max-w-xs">
                  Grupo de compra
                  <select
                    value={grupoEscolhido}
                    onChange={e => handleChangeGrupo(e.target.value)}
                    className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:border-blue-400"
                  >
                    <option value="">Automático (a partir do GMM estimado)</option>
                    <option value="mensal">Mensal</option>
                    <option value="bimestral">Bimestral</option>
                    <option value="trimestral">Trimestral</option>
                  </select>
                </label>
              </div>
            </Secao>
          )}

          {/* Etapa 4 — Veredito */}
          {indicadores && !indicadores.semHistorico && (
            <Secao numero={4} titulo="Veredito" subtitulo="Motor determinístico — nenhum número sai direto da IA">
              <button
                onClick={handleCalcular}
                disabled={calculando}
                className="flex items-center gap-1.5 px-4 py-2 border border-blue-300 bg-blue-50 text-blue-700 rounded-lg text-sm hover:bg-blue-100 disabled:opacity-40 disabled:cursor-not-allowed mb-4"
              >
                {calculando ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />}
                Calcular veredito
              </button>

              {veredito && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <span className={`inline-block text-sm font-semibold border rounded-lg px-3 py-1 mb-3 ${ROTULO_VEREDITO[veredito.veredito]?.className}`}>
                    {ROTULO_VEREDITO[veredito.veredito]?.label || veredito.veredito}
                  </span>

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm mb-4">
                    <div><span className="text-xs text-gray-500 block">F_web</span>{veredito.fWeb.toFixed(2)}</div>
                    <div><span className="text-xs text-gray-500 block">GMM Estimado</span>{veredito.gmmEstimado.toFixed(2)}</div>
                    <div><span className="text-xs text-gray-500 block">Grupo Final</span>{ROTULO_GRUPO[veredito.grupoFinal] || '—'}</div>
                    <div><span className="text-xs text-gray-500 block">Dias de Cobertura</span>{veredito.diasCobertura}</div>
                    <div><span className="text-xs text-gray-500 block">Quantidade Sugerida</span>{veredito.quantidadeSugerida} un.</div>
                    <div><span className="text-xs text-gray-500 block">Duração Estimada</span>{veredito.duracaoMeses != null ? `${veredito.duracaoMeses} meses` : '—'}</div>
                    <div><span className="text-xs text-gray-500 block">Valor Imobilizado</span>{formatarMoeda(veredito.valorImobilizado)}</div>
                  </div>

                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-gray-500 border-b border-gray-200">
                        <th className="text-left py-1.5 font-medium">Atributo</th>
                        <th className="text-left py-1.5 font-medium">Peso</th>
                        <th className="text-left py-1.5 font-medium">Nota</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {veredito.notasUsadas.map(n => (
                        <tr key={n.atributo}>
                          <td className="py-1.5 text-gray-700">{ATRIBUTOS_PESQUISA_WEB.find(a => a.chave === n.atributo)?.label || n.atributo}</td>
                          <td className="py-1.5 text-gray-600">{(n.peso * 100).toFixed(0)}%</td>
                          <td className="py-1.5 text-gray-600">{n.nota.toFixed(1)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  <div className="mt-5 pt-4 border-t border-gray-200">
                    <p className="text-sm text-gray-700 mb-2">Decisão</p>
                    <div className="flex flex-wrap gap-2 mb-3">
                      {['comprar', 'compra_teste', 'nao_comprar'].map(opcao => (
                        <button
                          key={opcao}
                          onClick={() => setDecisaoHumana(opcao)}
                          className={`px-3 py-1.5 rounded-lg text-sm border transition-colors ${
                            decisaoHumana === opcao
                              ? ROTULO_VEREDITO[opcao].className + ' font-semibold'
                              : 'border-gray-200 text-gray-600 hover:bg-gray-100'
                          }`}
                        >
                          {ROTULO_VEREDITO[opcao].label}
                        </button>
                      ))}
                    </div>
                    <textarea
                      value={observacao}
                      onChange={e => setObservacao(e.target.value)}
                      placeholder="Observação (opcional)"
                      rows={2}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-400 resize-none mb-3"
                    />
                    <button
                      onClick={handleSalvar}
                      disabled={!decisaoHumana || salvando}
                      className="flex items-center gap-1.5 px-4 py-2 border border-green-300 bg-green-50 text-green-700 rounded-lg text-sm hover:bg-green-100 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {salvando ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                      Salvar Avaliação
                    </button>
                  </div>
                </div>
              )}
            </Secao>
          )}

          {avaliacaoSalva && (
            <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-xl px-4 py-3 mb-4">
              <p className="text-sm text-green-800 flex items-center gap-1.5">
                <CheckCircle2 size={14} />Avaliação #{avaliacaoSalva.id} salva{usuario?.nome_completo ? ` por ${usuario.nome_completo}` : ''}.
              </p>
              <button
                onClick={resetarTudo}
                className="flex items-center gap-1.5 px-3 py-1.5 border border-green-300 bg-white text-green-700 rounded-lg text-sm hover:bg-green-100"
              >
                <RotateCcw size={13} />Nova avaliação
              </button>
            </div>
          )}
        </>
      )}

      {modalPesquisaIndisponivel && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={() => setModalPesquisaIndisponivel(false)}
        >
          <div
            className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-6 text-center"
            onClick={e => e.stopPropagation()}
          >
            <div className="inline-flex items-center justify-center w-12 h-12 bg-red-100 rounded-full mb-4">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Pesquisa na internet indisponível</h3>
            <p className="text-sm text-gray-600 mb-5">
              As notas dos atributos foram preenchidas como neutras (2,5). Ajuste manualmente cada slider
              se tiver informação sobre o produto.
            </p>
            <button
              onClick={() => setModalPesquisaIndisponivel(false)}
              className="px-5 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors"
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
